import React, { useCallback, useEffect } from 'react';
import { useCompleteOrderWithSelectedCard } from './useCompleteOrderWithSelectedCard';
import { useCompleteOrderWithCardOnTab } from './useCompleteOrderWithCardOnTab';
import { useCompleteOrderWithCash } from './useCompleteOrderWithCash';
import { useCompleteOrderWithGiftCard } from './useCompleteOrderWithGiftCard';
import { useCompleteOrderWithPaymentToken } from './useCompleteOrderWithPaymentToken';
import { useCompleteOrderWithMixedPayments } from './useCompleteOrderWithMixedPayments';
import { useCompleteOrderWithMixedSavedPayments } from './useCompleteOrderWithMixedSavedPayments';
import { CompleteOrderError } from '@olo-web/domain/orders/mutations/useCheckout';
import { useValidateOnCheckout } from './useValidateOnCheckout';
import {
  useCheckoutDispatch,
  useCheckoutState,
  useAlertDispatch,
  TCheckoutAction,
  TCustomerAction,
  TalertAction,
  useScreenSizeState,
  useCustomerDispatch,
} from '@olo-web/client-state';
import { useFeatureFlags, useGiftCardBalance, useMerchantPath } from '@olo-web/domain';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';
import { useGoToConfirmation } from '@olo-web/utils/common/hooks/useGoToConfirmation';
import { useMerchant, useMerchantOrderTypeDetails } from '@domain/merchants/queries';
import {
  useCurrentOrderTips,
  useToast,
  useIsScanAndPayPage,
  useIsTablePayPage,
  useIsDineIn,
  useHandleAsapLogicOnServer,
} from '@olo-web/utils/common/hooks';
import { usePaymentMethodValues } from '@olo-web/domain/payments/hooks';
import { EAlertTypes } from '@olo-web/types/enums';
import { useUpdateLocalPaymentType } from './useUpdateLocalPaymentType';
import { useIsSplitByAmount } from '@olo-web/utils/common/hooks/useIsSplitByAmount';
import { useRouter } from 'next/router';
import { useCompleteOrderOnPremise } from './useCompleteOrderOnPremise';
import { useTablePayConfirmationOptional } from '@olo-web/components/templates/Checkout/TablePayBill/TablePayConfirmationContext';
import { useCheckoutOrderTotal } from '@olo-web/domain/orders/hooks/useCheckoutOrderTotal';
import { useTipAmount } from '@olo-web/domain/orders/hooks/useTipAmount';
import { formatPrice } from '@olo-web/domain/orders/functions/formatPrice';
import { useCheckDetails } from '@olo-web/domain/orders/queries/useCheckDetails';

const checkoutOrderPostSubmitSuccessRef: { current: (() => void) | undefined } = {
  current: undefined,
};

type CheckoutConfirmationLabels = {
  amountPaidLabel: string;
  tipLabel: string;
  showTipLine: boolean;
};

const buildConfirmationLabels = (
  orderTotalLabel: string,
  tipAmountStr: string
): CheckoutConfirmationLabels => ({
  amountPaidLabel: orderTotalLabel,
  tipLabel: formatPrice(tipAmountStr),
  showTipLine: +tipAmountStr > 0,
});

const getCheckoutQueryParams = (
  isScanAndPayPage: boolean,
  checkId: string | string[] | undefined
): { checkId?: string } => {
  if (!isScanAndPayPage) {
    return {};
  }

  return {
    checkId: checkId as string,
  };
};

const shouldUseSplitFlow = ({
  selectedItemsAmount,
  hasTableQuery,
  tablePaySplitPaymentEnabled,
}: {
  selectedItemsAmount: number | null | undefined;
  hasTableQuery: boolean;
  tablePaySplitPaymentEnabled?: unknown;
}) => {
  return selectedItemsAmount != null || (hasTableQuery && Boolean(tablePaySplitPaymentEnabled));
};

const presentTablePayConfirmation = async ({
  isTablePayPage,
  tablePayConfirmation,
  confirmLabels,
  onDuringDisplay,
}) => {
  if (!isTablePayPage || !tablePayConfirmation) {
    return false;
  }

  await tablePayConfirmation.present({
    ...confirmLabels,
    onDuringDisplay,
  });
  return true;
};

const handlePostSubmitNavigation = async ({
  useSplitFlow,
  isTablePayPage,
  tablePayConfirmation,
  confirmLabels,
  queryParams,
  runSplitAftermath,
  navigateTo,
  goToConfirmation,
  onAfterHandled = undefined,
}) => {
  if (useSplitFlow) {
    const runSplitAndNavigate = async () => {
      const path = await runSplitAftermath();
      if (path) {
        await navigateTo(path);
      }
    };

    const presented = await presentTablePayConfirmation({
      isTablePayPage,
      tablePayConfirmation,
      confirmLabels,
      onDuringDisplay: runSplitAndNavigate,
    });

    if (!presented) {
      await runSplitAndNavigate();
    }
    onAfterHandled?.();
    return;
  }

  const presented = await presentTablePayConfirmation({
    isTablePayPage,
    tablePayConfirmation,
    confirmLabels,
    onDuringDisplay: () => goToConfirmation(queryParams),
  });

  if (!presented) {
    void goToConfirmation(queryParams);
  }
  onAfterHandled?.();
};

const handleSubmitOrderError = (
  err: CompleteOrderError,
  checkoutDispatch: React.Dispatch<TCheckoutAction>,
  alertDispatch: React.Dispatch<TalertAction>,
  toast,
  isSplitByAmount = false,
  customerDispatch: React.Dispatch<TCustomerAction>,
  refetchOrder: (options?: any) => Promise<any>
) => {
  checkoutDispatch({ type: 'COMPLETE_SUBMISSION' });

  if (err?.code === 'INACTIVE_DISCOUNT_ERROR') {
    customerDispatch({
      type: 'REMOVE_PROMO',
      payload: err?.additionalData?.discountIds[0],
    });
    refetchOrder();
  }

  if (err?.code === 'PACING_CAPACITY_ERROR') {
    alertDispatch({
      type: 'OPEN_ALERT',
      payload: {
        alertKey: err?.alternateDateTimes?.length
          ? EAlertTypes.PACING_CAPACITY_ALERT
          : EAlertTypes.NO_AVAILABLE_TIMES_ALERT,
        alertContext: err?.alternateDateTimes,
      },
    });
    return;
  }
  checkoutDispatch({ type: 'REMOVE_PAYMENT' });

  if (err?.message.includes('AVS REJECTED')) {
    toast({
      status: 'error',
      title: "There's an issue with your card",
      description: 'Please update the ZIP code to match your card information',
      isClosable: true,
      duration: null,
      variant: 'left-accent',
    });
    console.error('[Checkout Error]', err);
  } else if (
    !(isSplitByAmount && err?.message === 'Payment amount total cannot exceed the check balance')
  ) {
    toast({
      status: 'error',
      title: 'Error placing your order',
      description: err?.message ?? 'Something went wrong',
      isClosable: true,
      duration: null,
      variant: 'left-accent',
    });
  }
  console.error('[Checkout Error]', err);
};

/** Shared by card `placeOrder` and wallet `usePlaceOrderWithPaymentToken` for table split-pay aftermath. */
async function runTableSplitPayAftermath({
  refetchChecks,
  checkoutDispatch,
  merchantPath,
  router,
  isTablePayPage,
}) {
  const checkResults = await refetchChecks();
  const checks = checkResults.map((result) => result.data).filter(Boolean) as IOrder[];

  const orderBalance = checks.reduce((sum, check) => {
    return sum + Number(check.balanceDueAmount ?? 0);
  }, 0);
  const hasMultipleChecks = checks.length > 1;
  const hasMultipleCheckIds = (checks[0]?.allCheckIds?.length ?? 0) > 1;
  checkoutDispatch({ type: 'RESET_SELECTED_ITEMS' });
  checkoutDispatch({ type: 'REMOVE_PAYMENT' });
  checkoutDispatch({ type: 'COMPLETE_SUBMISSION' });
  checkoutDispatch({ type: 'SET_ORDER_NOTE', payload: '' });
  checkoutDispatch({ type: 'CLEAR_GIFTCARD' });

  const tableId = router.query.tableId;
  const goToTablePayHub =
    Boolean(tableId) &&
    orderBalance > 0 &&
    (hasMultipleCheckIds || hasMultipleChecks || isTablePayPage);

  if (goToTablePayHub) {
    return `${merchantPath}/pay/table/${String(tableId)}`;
  }

  return null;
}

const useSubmitOrderWithPaymentToken = () => {
  const { data: giftCard } = useGiftCardBalance();
  const completeOrderWithMixedPayments = useCompleteOrderWithMixedPayments();
  const completeOrderWithPaymentToken = useCompleteOrderWithPaymentToken();
  return (paymentToken, mobileCustomer?: ICustomer) => {
    if (giftCard) {
      return completeOrderWithMixedPayments(paymentToken, mobileCustomer);
    }
    return completeOrderWithPaymentToken(paymentToken, mobileCustomer);
  };
};

const useSubmitOrderWithSavedCard = () => {
  const { data: giftCard } = useGiftCardBalance();
  const completeOrderWithMixedSavedPayments = useCompleteOrderWithMixedSavedPayments();
  const completeOrderWithSelectedCard = useCompleteOrderWithSelectedCard();
  return useCallback(() => {
    if (giftCard) {
      return completeOrderWithMixedSavedPayments();
    }
    return completeOrderWithSelectedCard();
  }, [completeOrderWithMixedSavedPayments, completeOrderWithSelectedCard, giftCard]);
};

const useSubmitOrderWithCardOnTab = () => {
  const completeOrderWithCardOnTab = useCompleteOrderWithCardOnTab();
  return useCallback(() => {
    return completeOrderWithCardOnTab();
  }, [completeOrderWithCardOnTab]);
};
export const usePlaceOrderWithPaymentToken = () => {
  const submitOrderWithPaymentToken = useSubmitOrderWithPaymentToken();
  const goToConfirmation = useGoToConfirmation({ invalidatePreviousOrders: true });
  const { notify } = useToast();
  const checkoutDispatch = useCheckoutDispatch();
  const customerDispatch = useCustomerDispatch();
  const alertDispatch = useAlertDispatch();
  const updateLocalPaymentType = useUpdateLocalPaymentType();
  const isSplitByAmount = useIsSplitByAmount();
  const isScanAndPayPage = useIsScanAndPayPage();
  const isTablePayPage = useIsTablePayPage();
  const tablePayConfirmation = useTablePayConfirmationOptional();
  const orderTotalLabel = useCheckoutOrderTotal();
  const tipAmountStr = useTipAmount();
  const { refetch: refetchOrder, data: order } = useOrder();
  const { data: merchant } = useMerchant();
  const { refetchAll: refetchChecks } = useCheckDetails(
    merchant?.merchantId,
    order?.allCheckIds ?? []
  );
  const merchantPath = useMerchantPath();
  const router = useRouter();
  const handleAsapLogicOnServer = useHandleAsapLogicOnServer();
  const { data: flags } = useFeatureFlags();
  const { selectedItemsAmount } = useCheckoutState();

  return async (paymentToken, mobileCustomer?: ICustomer) => {
    checkoutDispatch({ type: 'START_SUBMISSION' });

    try {
      updateLocalPaymentType();
      await submitOrderWithPaymentToken(paymentToken, mobileCustomer);

      const postSubmitOnSuccess = checkoutOrderPostSubmitSuccessRef.current;

      const queryParams = getCheckoutQueryParams(isScanAndPayPage, router.query.checkId);
      const confirmLabels = buildConfirmationLabels(orderTotalLabel, tipAmountStr);

      if (!isScanAndPayPage) {
        void goToConfirmation(queryParams);
        postSubmitOnSuccess?.();
        return;
      }

      const useSplitFlow = shouldUseSplitFlow({
        selectedItemsAmount,
        hasTableQuery: !!router.query.table,
        tablePaySplitPaymentEnabled: flags?.enableTablePaySplitPayment,
      });

      const runSplitAftermath = () =>
        runTableSplitPayAftermath({
          refetchChecks,
          checkoutDispatch,
          merchantPath,
          router,
          isTablePayPage,
        });

      await handlePostSubmitNavigation({
        useSplitFlow,
        isTablePayPage,
        tablePayConfirmation,
        confirmLabels,
        queryParams,
        runSplitAftermath,
        navigateTo: (path) => router.push(path),
        goToConfirmation,
        onAfterHandled: () => postSubmitOnSuccess?.(),
      });
    } catch (err) {
      if (!handleAsapLogicOnServer) {
        handleSubmitOrderError(
          err,
          checkoutDispatch,
          alertDispatch,
          notify,
          isSplitByAmount,
          customerDispatch,
          refetchOrder
        );
      }
    }
  };
};

export const useSubmitOrder = () => {
  const submitOrderWithSavedCard = useSubmitOrderWithSavedCard();
  const submitOrderWithCardOnTab = useSubmitOrderWithCardOnTab();
  const completeOrderWithCash = useCompleteOrderWithCash();
  const conpleteOrderOnPremise = useCompleteOrderOnPremise();
  const completeOrderWithGiftCard = useCompleteOrderWithGiftCard();
  const submitOrderWithPaymentToken = useSubmitOrderWithPaymentToken();
  const {
    isCash,
    isCreditCard,
    isCreditCardOnTab,
    giftCardCoversEntireOrderAmount,
    isZeroChargeOrder,
    isMobilePayment,
    isPayOnPremise,
  } = usePaymentMethodValues();
  const { selectedCard, addedPayment } = useCheckoutState();
  const { notify } = useToast();

  // We do not need to invoke collectJS for these types of checkouts
  return useCallback(() => {
    if (isPayOnPremise) {
      return conpleteOrderOnPremise();
    }

    if (isCash || isZeroChargeOrder) {
      return completeOrderWithCash();
    }

    if (giftCardCoversEntireOrderAmount) {
      return completeOrderWithGiftCard();
    }

    if (isCreditCardOnTab) {
      return submitOrderWithCardOnTab();
    }

    if (isCreditCard && selectedCard?.id) {
      return submitOrderWithSavedCard();
    }

    if ((isCreditCard || isMobilePayment) && addedPayment?.token) {
      return submitOrderWithPaymentToken(addedPayment?.token);
    }

    // We should never reach this because one of the above should be true, but just incase
    notify({
      status: 'error',
      title: "There's an issue with your payment method",
      description: 'Please refresh the page and give us a call if the issue persists',
      position: 'top-right',
      variant: 'left-accent',
    });
    return Promise.reject();
  }, [
    addedPayment?.token,
    completeOrderWithCash,
    completeOrderWithGiftCard,
    giftCardCoversEntireOrderAmount,
    isCash,
    isCreditCard,
    isCreditCardOnTab,
    isMobilePayment,
    isZeroChargeOrder,
    isPayOnPremise,
    notify,
    selectedCard?.id,
    submitOrderWithPaymentToken,
    submitOrderWithSavedCard,
    submitOrderWithCardOnTab,
    conpleteOrderOnPremise,
  ]);
};

interface IConfig {
  onSuccess?: () => any;
  onError?: () => void;
}

export const usePlaceOrder = ({ onSuccess, onError }: IConfig = {}) => {
  const router = useRouter();
  const { refetch: fetchMerchant, data: merchant } = useMerchant();
  const { refetch: refetchOrder, data: order } = useOrder();
  const submitOrder = useSubmitOrder();
  const checkoutDispatch = useCheckoutDispatch();
  const customerDispatch = useCustomerDispatch();
  const { notify, toast } = useToast();
  const goToConfirmation = useGoToConfirmation({ invalidatePreviousOrders: true });
  const alertDispatch = useAlertDispatch();
  const isScanAndPayPage = useIsScanAndPayPage();
  const isTablePayPage = useIsTablePayPage();
  const tablePayConfirmation = useTablePayConfirmationOptional();
  const orderTotalLabel = useCheckoutOrderTotal();
  const tipAmountStr = useTipAmount();
  const isSplitByAmount = useIsSplitByAmount();
  const { data: merchantOrderDetails } = useMerchantOrderTypeDetails();
  const { selectedTips, customTipsMode, customTipsPercentage, customTipsAmount } =
    useCurrentOrderTips();
  const { isMd } = useScreenSizeState();
  const validateOnCheckout = useValidateOnCheckout();
  const tipToastId = 'no-tip-selected';
  const { isCash, isPayOnPremise } = usePaymentMethodValues();
  const handleAsapLogicOnServer = useHandleAsapLogicOnServer();
  const { selectedItemsAmount } = useCheckoutState();
  const { refetchAll: refetchChecks } = useCheckDetails(
    merchant?.merchantId,
    order?.allCheckIds ?? []
  );
  const { data: flags } = useFeatureFlags();
  const merchantPath = useMerchantPath();

  useEffect(() => {
    if (selectedTips) toast.close(tipToastId);
  }, [selectedTips, toast]);

  useEffect(() => {
    checkoutOrderPostSubmitSuccessRef.current = onSuccess;
    return () => {
      checkoutOrderPostSubmitSuccessRef.current = undefined;
    };
  }, [onSuccess]);

  const updateLocalPaymentType = useUpdateLocalPaymentType();
  const isDinein = useIsDineIn();

  const placeOrder = useCallback(async () => {
    // If it's a mobile payment we have no choice but to handle everything on the otherside of the
    // collectJS callback, so the logic that follows this is duplicated there anyways
    checkoutDispatch({ type: 'START_SUBMISSION' });

    try {
      await validateOnCheckout();
    } catch (err) {
      return;
    }

    const invalidTip =
      (!selectedTips && !isPayOnPremise) ||
      (selectedTips === 'custom' &&
        ((customTipsMode === 'percentages' && !customTipsPercentage) ||
          (customTipsMode === 'amount' && !customTipsAmount) ||
          (selectedTips === 'custom' && !customTipsMode)));

    if (merchantOrderDetails?.tippable && invalidTip && !isCash) {
      if (!toast.isActive(tipToastId)) {
        notify({
          id: tipToastId,
          title: 'Please select your tip amount',
          status: 'error',
          variant: 'left-accent',
          position: isMd ? 'top-right' : 'top',
          isClosable: true,
        });
      }
      checkoutDispatch({ type: 'COMPLETE_SUBMISSION' });

      const el = document.getElementById('tips-selection-button-group');
      if (el) {
        el.scrollIntoView();
        el.focus();
      }
      onError?.();
      return;
    }

    try {
      updateLocalPaymentType();

      await submitOrder();

      const confirmLabels = buildConfirmationLabels(orderTotalLabel, tipAmountStr);
      const queryParams = getCheckoutQueryParams(isScanAndPayPage, router.query.checkId);
      const useSplitFlow = shouldUseSplitFlow({
        selectedItemsAmount,
        hasTableQuery: !!router.query.table,
        tablePaySplitPaymentEnabled: flags?.enableTablePaySplitPayment,
      });

      const runSplitAftermath = () =>
        runTableSplitPayAftermath({
          refetchChecks,
          checkoutDispatch,
          merchantPath,
          router,
          isTablePayPage,
        });

      await handlePostSubmitNavigation({
        useSplitFlow,
        isTablePayPage,
        tablePayConfirmation,
        confirmLabels,
        queryParams,
        runSplitAftermath,
        navigateTo: (path) => router.push(path),
        goToConfirmation,
        onAfterHandled: onSuccess,
      });
    } catch (err) {
      if (!handleAsapLogicOnServer) {
        handleSubmitOrderError(
          err,
          checkoutDispatch,
          alertDispatch,
          notify,
          isSplitByAmount,
          customerDispatch,
          refetchOrder
        );
      }
      onError?.();
      if (isDinein) {
        refetchOrder();
      }
    }
  }, [
    checkoutDispatch,
    selectedTips,
    isPayOnPremise,
    customTipsMode,
    customTipsPercentage,
    customTipsAmount,
    merchantOrderDetails?.tippable,
    isCash,
    validateOnCheckout,
    toast,
    onError,
    notify,
    isMd,
    updateLocalPaymentType,
    submitOrder,
    selectedItemsAmount,
    router,
    flags?.enableTablePaySplitPayment,
    refetchChecks,
    refetchOrder,
    onSuccess,
    merchantPath,
    isScanAndPayPage,
    isTablePayPage,
    tablePayConfirmation,
    orderTotalLabel,
    tipAmountStr,
    goToConfirmation,
    handleAsapLogicOnServer,
    isDinein,
    alertDispatch,
    isSplitByAmount,
    customerDispatch,
  ]);

  return useCallback(async () => {
    if (isDinein || isScanAndPayPage) {
      await placeOrder();
    } else {
      fetchMerchant().then((data) => {
        if (data.data.isOrderingAvailable) {
          placeOrder();
        }
      });
    }
  }, [isDinein, isScanAndPayPage, placeOrder, fetchMerchant]);
};
